"use client";

import { CalendarIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { type NumberFormatValues, PatternFormat, type SourceInfo } from "react-number-format";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { isValidDate, toDisplayDate, parseDisplayDate } from "@/lib/utils/dates";

type Props = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  error?: boolean;
  placeholder?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  isModal?: boolean;
  ref?: React.Ref<HTMLInputElement>;
};

// The bare digits the ##/##/#### mask below expects, e.g. "10152026"
const toDigits = (date: Date | undefined) => (isValidDate(date) ? toDisplayDate(date).replace(/\D/g, "") : "");

/**
 * Date input that accepts typed MM/DD/YYYY entry or a pick from the calendar popover.
 *
 * Incomplete or impossible typed dates are reported as an Invalid Date (rather than undefined),
 * so form schemas can show "Please enter a valid date" instead of treating the field as empty.
 */
export const DatePicker = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  id,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  isModal,
  ref,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [digits, setDigits] = useState(() => toDigits(value));
  const [prevValue, setPrevValue] = useState(value);

  // Keep the text in step with value changes from outside (e.g. form reset).
  // Object.is treats two Invalid Dates (NaN) as equal, so partial typing doesn't loop.
  if (!Object.is(prevValue?.getTime(), value?.getTime())) {
    setPrevValue(value);
    // An Invalid Date comes from the user's own partial typing, so keep what they typed
    if (!value || isValidDate(value)) {
      setDigits(toDigits(value));
    }
  }

  const handleTextChange = ({ value: nextDigits, formattedValue }: NumberFormatValues, { source }: SourceInfo) => {
    if (source !== "event") return;

    setDigits(nextDigits);
    onChange(nextDigits ? (parseDisplayDate(formattedValue) ?? new Date(NaN)) : undefined);
  };

  const handleSelect = (date: Date | undefined) => {
    setDigits(toDigits(date));
    onChange(date);
    setOpen(false);
  };

  const handleClear = () => {
    setDigits("");
    onChange(undefined);
    // Close so focus returns to the calendar button rather than being lost when the Clear button unmounts
    setOpen(false);
  };

  const selectedDate = isValidDate(value) ? value : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={isModal}>
      <PopoverAnchor asChild>
        <div className="relative">
          <PatternFormat
            customInput={Input}
            getInputRef={ref}
            format="##/##/####"
            mask="_"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={digits}
            valueIsNumericString
            onValueChange={handleTextChange}
            id={id}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedby}
            className="pr-10"
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Open calendar"
              className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:bg-transparent"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={handleSelect}
          initialFocus
        />
        {selectedDate && (
          <div className="flex justify-end p-2">
            <Button type="button" variant="outline" className="rounded-3xl" onClick={handleClear}>
              <XIcon className="mr-2 h-4" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
